/**
 * Slice 20b — VA Unit Check marks per cleaning task.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";

export type UnitCheckStatus = "here" | "missing" | "broken";

export interface UnitCheckMark {
  id: string;
  cleaning_task_id: string;
  unit_item_id: string | null;
  /** The mark can also be on a supply or an appliance. */
  inventory_item_id?: string | null;
  appliance_id?: string | null;
  status: UnitCheckStatus;
  proof_photo_url: string | null;
  issue_report_id: string | null;
  /** Slice 58 — the owner's note on this row of the report. */
  note?: string | null;
  created_at: string;
  updated_at: string;
}

export function useUnitCheckMarks(cleaningTaskId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.unitCheckMarks.byTask(cleaningTaskId ?? ""),
    queryFn: async (): Promise<UnitCheckMark[]> => {
      if (!cleaningTaskId) return [];
      const { data, error } = await supabase
        .from("unit_check_marks")
        .select("*")
        .eq("cleaning_task_id", cleaningTaskId);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        ...row,
        status: row.status as UnitCheckStatus,
      }));
    },
    enabled: !!cleaningTaskId,
  });
}

export function useUpsertUnitCheckMark() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      cleaningTaskId: string;
      /** Which kind of item this mark is on; unit items by default. */
      itemKind?: "unit" | "supply" | "appliance";
      unitItemId: string;
      status: UnitCheckStatus;
      proofPhotoUrl?: string | null;
      issueReportId?: string | null;
      note?: string | null;
    }) => {
      const kind = input.itemKind ?? "unit";
      const keyCol = kind === "supply" ? "inventory_item_id" : kind === "appliance" ? "appliance_id" : "unit_item_id";
      const { data: existing, error: findError } = await supabase
        .from("unit_check_marks")
        .select("id, issue_report_id")
        .eq("cleaning_task_id", input.cleaningTaskId)
        .eq(keyCol as "unit_item_id", input.unitItemId)
        .maybeSingle();
      if (findError) throw findError;

      if (existing?.id) {
        const patch: {
          status: UnitCheckStatus;
          proof_photo_url?: string | null;
          issue_report_id?: string | null;
          note?: string | null;
        } = { status: input.status };
        if (input.note !== undefined) patch.note = input.note;
        if (input.proofPhotoUrl !== undefined) patch.proof_photo_url = input.proofPhotoUrl;
        if (input.issueReportId !== undefined) {
          patch.issue_report_id = input.issueReportId;
        } else if (existing.issue_report_id) {
          patch.issue_report_id = existing.issue_report_id;
        }
        const { data, error } = await supabase
          .from("unit_check_marks")
          .update(patch)
          .eq("id", existing.id)
          .select()
          .single();
        if (error) throw error;
        return { ...data, status: data.status as UnitCheckStatus } as UnitCheckMark;
      }

      const { data, error } = await supabase
        .from("unit_check_marks")
        .insert({
          cleaning_task_id: input.cleaningTaskId,
          [keyCol]: input.unitItemId,
          status: input.status,
          proof_photo_url: input.proofPhotoUrl ?? null,
          issue_report_id: input.issueReportId ?? null,
          ...(input.note !== undefined ? { note: input.note } : {}),
        } as never)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as UnitCheckMark;
    },
    onSuccess: (row) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.unitCheckMarks.byTask(row.cleaning_task_id),
      });
    },
  });
}

export function useClearUnitCheckMarks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (cleaningTaskId: string) => {
      const { error } = await supabase
        .from("unit_check_marks")
        .delete()
        .eq("cleaning_task_id", cleaningTaskId);
      if (error) throw error;
      return cleaningTaskId;
    },
    onSuccess: (cleaningTaskId) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.unitCheckMarks.byTask(cleaningTaskId),
      });
    },
  });
}

/**
 * Keep the issue filed by a Unit check in step with the mark.
 * Marking an item Here again closes the issue; marking it Missing / Broken again reopens it
 * (and fixes its wording when switching between Missing and Broken).
 */
export async function syncUnitCheckIssueReport(input: {
  reportId: string;
  status: UnitCheckStatus;
  itemName: string;
}): Promise<void> {
  const { reportId, status, itemName } = input;
  if (status === "here") {
    const { error } = await supabase
      .from("maintenance_issue_reports")
      .update({
        status: "resolved",
        resolved_at: new Date().toISOString(),
        resolution_note: null,
      })
      .eq("id", reportId);
    if (error) throw error;
    return;
  }
  const label = status === "broken" ? "Broken" : "Missing";
  const { error } = await supabase
    .from("maintenance_issue_reports")
    .update({
      status: "open",
      resolved_at: null,
      resolved_by: null,
      resolution_note: null,
      response: status === "broken" ? "needs_repair" : "no",
      notes: `${label}: ${itemName}`,
      task_description: `${label}: ${itemName}`,
    })
    .eq("id", reportId);
  if (error) throw error;
}

/** Close the issues behind these marks (used by Reset, which deletes the marks). */
export async function resolveUnitCheckIssueReports(reportIds: string[]): Promise<void> {
  if (reportIds.length === 0) return;
  const { error } = await supabase
    .from("maintenance_issue_reports")
    .update({ status: "resolved", resolved_at: new Date().toISOString(), resolution_note: null })
    .in("id", reportIds);
  if (error) throw error;
}
